import { preventedScrollClass } from '../helpers';

import { getElement } from './dom';
import { IN_BROWSER } from './globals';

const scrollTargets = !IN_BROWSER
  ? []
  : [null, document, document.body, document.scrollingElement, document.documentElement];

export function getScrollParent(el?: HTMLElement) {
  while (el) {
    if (hasScrollbar(el)) return el;
    el = el.parentElement!;
  }

  return document.scrollingElement as HTMLElement;
}

export function getScrollParents(el?: Element | null, stopAt?: Element | null) {
  const elements: HTMLElement[] = [];

  if (stopAt && el && !stopAt.contains(el)) return elements;

  while (el) {
    if (hasScrollbar(el)) elements.push(el as HTMLElement);
    if (el === stopAt) break;
    el = el.parentElement!;
  }

  return elements;
}

export function getScrollTarget(el?: HTMLElement, targetEl?: HTMLElement | string) {
  let target = getElement(targetEl);

  if (target === undefined) {
    if (el === undefined || el === null) return window;

    target = el.closest('.cds-scrollable');
  }

  return scrollTargets.includes(target) ? window : target;
}

export function hasScrollbar(el?: Element | null) {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;

  const style = window.getComputedStyle(el);
  return style.overflowY === 'scroll' || (style.overflowY === 'auto' && el.scrollHeight > el.clientHeight);
}

let cached: number;
export function getScrollBarSize(fresh?: boolean) {
  if (!IN_BROWSER) {
    return 0;
  }

  if (fresh || cached === undefined) {
    const inner = document.createElement('div');
    inner.style.width = '100%';
    inner.style.height = '200px';

    const outer = document.createElement('div');
    const outerStyle = outer.style;

    outerStyle.position = 'absolute';
    outerStyle.top = '0';
    outerStyle.left = '0';
    outerStyle.pointerEvents = 'none';
    outerStyle.visibility = 'hidden';
    outerStyle.width = '200px';
    outerStyle.height = '150px';
    outerStyle.overflow = 'hidden';

    outer.appendChild(inner);

    document.body.appendChild(outer);

    const widthContained = inner.offsetWidth;
    outer.style.overflow = 'scroll';
    let widthScroll = inner.offsetWidth;

    if (widthContained === widthScroll) {
      widthScroll = outer.clientWidth;
    }

    document.body.removeChild(outer);

    cached = widthContained - widthScroll;
  }
  return cached;
}

export function getTargetScrollBarSize(target: HTMLElement) {
  if (!IN_BROWSER || !target || !(target instanceof Element)) {
    return { width: 0, height: 0 };
  }

  const { width, height } = getComputedStyle(target, '::-webkit-scrollbar');
  return {
    width: ensureSize(width),
    height: ensureSize(height)
  };
}

export function getOffset(node: any) {
  const box = node.getBoundingClientRect();
  const docElem = document.documentElement;

  return {
    left: box.left + (window.pageXOffset || docElem.scrollLeft) - (docElem.clientLeft || document.body.clientLeft || 0),
    top: box.top + (window.pageYOffset || docElem.scrollTop) - (docElem.clientTop || document.body.clientTop || 0)
  };
}

export function getVerticalScrollPosition(el?: HTMLElement | Window) {
  return el === window
    ? window.pageYOffset || window.scrollY || document.body.scrollTop || 0
    : (el as HTMLElement)?.scrollTop;
}

export function getHorizontalScrollPosition(el?: HTMLElement | Window) {
  return el === window
    ? window.pageXOffset || window.scrollX || document.body.scrollLeft || 0
    : (el as HTMLElement)?.scrollLeft;
}

export function hasPreventedScroll() {
  return document.documentElement.classList.contains(preventedScrollClass);
}

function ensureSize(str: string) {
  const match = str.match(/^(.*)px$/);
  const value = Number(match?.[1]);
  return Number.isNaN(value) ? getScrollBarSize() : value;
}
